import { useEffect, useState } from 'react'

import { faEllipsis, faSpinnerThird } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useIntl } from 'react-intl'

import { Popover, Tooltip } from '@b-stock/fe-core/bstock-react'

import type { PanelAction } from '../types'
import { TooltipLabel } from '../ui'
import { useIsRowVisible } from '../useIsRowVisible'
import {
  DropdownPanel,
  DropdownRow,
  IconButton,
  RowDescription,
  RowLabel,
  RowText,
  StatusPanel,
} from './ui'

/**
 * The actions that did not fit inline, behind one ellipsis button.
 *
 * The panel goes through a portal to the document rather than being drawn in the
 * cell: AG Grid clips cell overflow, so one rendered in place would be cut off at
 * the row boundary. fe-core `Popover` does the portal, and flips or shifts the
 * panel to keep it whole against the bottom of the grid and of the viewport.
 *
 * `Popover`'s outside-click is also what keeps one dropdown open at a time:
 * another row's trigger is an outside click to this one, so the first closes as
 * the second opens. And the open state is this component's own, so a row
 * scrolled out of the virtualized grid takes its dropdown with it rather than
 * leaving one floating over the rows that replace it.
 *
 * Scrolling moves the panel with its row, and hides it once the row is clipped
 * away — see `useIsRowVisible`.
 *
 * No `role="menu"`: that role promises arrow-key navigation, and nothing the
 * panel renders in a cell is keyboard-reachable at all — AG Grid's Tab moves
 * between cells, not into them. A plain list of buttons is what a virtual cursor
 * reads correctly, which is what fe-core's own `PopupMenu` settles on too.
 *
 * A click still pending once the dropdown has closed shows on the ellipsis
 * instead: a spinner stands in for its icon, and a status message anchors its
 * popover there, as it would to an inline icon.
 */
export const ActionMenuDropdown = ({ actions }: { actions: PanelAction[] }) => {
  const { formatMessage } = useIntl()
  const [isOpen, setIsOpen] = useState(false)
  const [buttonElement, setButtonElement] = useState<HTMLButtonElement | null>(
    null
  )

  const isPending = actions.some((action) => action.isPending)
  const answeredAction = actions.find((action) => action.statusMessage)

  const isRowVisible = useIsRowVisible(
    isOpen || answeredAction ? buttonElement : null
  )

  const label = formatMessage({
    id: 'Listings.ListView.actionPanel.menu.moreActions',
  })

  useEffect(() => {
    if (!isOpen) return

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false)
    }
    window.addEventListener('keydown', closeOnEscape)
    return () => window.removeEventListener('keydown', closeOnEscape)
  }, [isOpen])

  return (
    <>
      <IconButton
        aria-busy={isPending}
        aria-expanded={isOpen}
        aria-label={label}
        onClick={() => setIsOpen(!isOpen)}
        ref={setButtonElement}
        type="button"
      >
        <FontAwesomeIcon
          icon={isPending ? faSpinnerThird : faEllipsis}
          spin={isPending}
        />
      </IconButton>

      {!isOpen && !answeredAction && (
        <Tooltip referenceElement={buttonElement}>
          <TooltipLabel>{label}</TooltipLabel>
        </Tooltip>
      )}

      {answeredAction && (
        <Popover
          open
          options={{ placement: 'bottom-end' }}
          referenceElement={buttonElement}
          setOpen={(isStatusOpen) => {
            if (!isStatusOpen) answeredAction.dismissStatusMessage()
          }}
        >
          <StatusPanel $isRowVisible={isRowVisible} role="status">
            {answeredAction.statusMessage}
          </StatusPanel>
        </Popover>
      )}

      <Popover
        open={isOpen}
        options={{ placement: 'bottom-end' }}
        referenceElement={buttonElement}
        setOpen={setIsOpen}
      >
        <DropdownPanel $isRowVisible={isRowVisible}>
          {actions.map((action) => (
            <DropdownRow
              aria-busy={action.isPending}
              aria-disabled={!action.isAvailable}
              aria-label={action.label}
              key={action.id}
              onClick={() => {
                // Swallowed rather than prevented by the `disabled` attribute,
                // which would also mute the row's own hover.
                if (!action.isAvailable || action.isPending) return
                action.onClick()
                setIsOpen(false)
              }}
              type="button"
            >
              <FontAwesomeIcon
                icon={action.isPending ? faSpinnerThird : action.icon}
                spin={action.isPending}
              />
              <RowText>
                <RowLabel>{action.label}</RowLabel>
                <RowDescription>{action.description}</RowDescription>
              </RowText>
            </DropdownRow>
          ))}
        </DropdownPanel>
      </Popover>
    </>
  )
}
